<template>
  <div class="w-full relative" ref="dropdownContainer">
    <FormButton type="secondary" fullWidth @click="toggleVisible" size="md">
      <div v-if="!source" class="flex justify-start text-left w-full">
        Select an option
        <IconBase fill="none" class="ml-auto mr-0">
          <IconChevronUpDown />
        </IconBase>
      </div>
      <div :title="source.name" v-else class="justify-start text-left w-full flex items-center">
        <span class="max-w-[95%] truncate">{{ source.name }}</span>
        <IconBase fill="none" class="ml-auto" >
          <IconChevronUpDown />
        </IconBase>
      </div>
    </FormButton>
    <div v-if="visible" class="select-source-wrapper max-w-full mt-2 p-2">
      <TabGroup type="segmented" :cols="2" custom-classes="mb-2">
        <TabItem
          v-for="tab in sourceTabs"
          :key="tab.id"
          :is-active="activeSourceTab === tab.id"
          @click="activeSourceTab = tab.id"
        >
          <span class="truncate">{{ tab.label }}</span>
        </TabItem>
      </TabGroup>
      <div>
        <div v-show="activeSourceTab === 'playout'">
          <div v-if="playoutList && playoutList.length > 0" class="select-source-list-container">
            <ul class="list-none text-500 select-source-list mt-1">
              <li v-for="(stream, index) in playoutList" :key="index">
                <Tooltip :active="stream.destination" :content="stream.destination ? 'Destination is already set for this.' : ''">
                  <FormButton :title="stream.name" :disabled="stream.destination" @click="setSource(stream)"
                    :custom-classes="'w-full flex items-center text-left px-3 py-2 rounded-md text-surface-9 hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'">
                    <span class="truncate">{{ stream.name }}</span>
                    <span v-if="stream.destination" class="ml-auto pl-3 shrink-0">
                      <IconBase fill="none" class="text-surface-7">
                        <IconTV />
                      </IconBase>
                    </span>
                  </FormButton>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div v-else class="text-center">
            <div class="flex justify-center mb-3 mt-3">
              <IconBase fill="none" width="64" height="64" widthViewBox="64" heightViewBox="64">
                <IconStreamsEmptyPlaceholderImage />
              </IconBase>
            </div>
            <p class="mb-2 text-dark-9 text-s-s">Do not have any TV Playout.</p>
            <FormButton type="light" size="sm" @click="createNewPlayout" class="mb-1">Create new playout</FormButton>
          </div>
        </div>
        <div v-show="activeSourceTab === 'transcoder'">
          <div v-if="transcoderList && transcoderList.length > 0" class="select-source-list-container">
            <ul class="list-none text-500 select-source-list mt-1">
              <li v-for="(transcoder, index) in transcoderList" :key="index">
                <Tooltip :active="transcoder.destination" :content="transcoder.destination ? 'Destination is already set for this.' : ''">
                  <FormButton :title="transcoder.name" :disabled="transcoder.destination" @click="setSource(transcoder)"
                    :custom-classes="'w-full flex items-center text-left px-3 py-2 rounded-md text-surface-9 hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'">
                    <span class="truncate">{{ transcoder.name }}</span>
                    <span v-if="transcoder.destination" class="ml-auto pl-3 shrink-0">
                      <IconBase fill="none" class="text-surface-7">
                        <IconTranscoder />
                      </IconBase>
                    </span>
                  </FormButton>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div v-else class="text-center">
            <div class="flex justify-center mb-3 mt-3">
              <IconBase fill="none" width="64" height="64" widthViewBox="64" heightViewBox="64">
                <IconStreamsEmptyPlaceholderImage />
              </IconBase>
            </div>
            <p class="mb-2 text-dark-9 text-s-s">Do not have any Transcoder.</p>
            <FormButton type="light" fullWidth size="sm" @click="createNewTranscoder" class="mb-1">Create new transcoder
            </FormButton>
          </div>
        </div>
        <div v-show="activeSourceTab === 'livestreams'">
          <div v-if="livestreamList && livestreamList.length > 0" class="select-source-list-container">
            <ul class="list-none text-500 select-source-list mt-1">
              <li v-for="(livestream, index) in livestreamList" :key="index">
                <Tooltip :active="livestream.destination" :content="livestream.destination ? 'Destination is already set for this.' : ''">
                  <FormButton :title="livestream.name" :disabled="livestream.destination" @click="setSource(livestream)"
                    :custom-classes="'w-full flex items-center text-left px-3 py-2 rounded-md text-surface-9 hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'">
                    <span class="truncate">{{ livestream.name }}</span>
                    <span v-if="livestream.destination" class="ml-auto pl-3 shrink-0">
                      <IconBase fill="none" class="text-surface-7">
                        <IconTV />
                      </IconBase>
                    </span>
                  </FormButton>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div v-else class="text-center">
            <div class="flex justify-center mb-3 mt-3">
              <IconBase fill="none" width="64" height="64" widthViewBox="64" heightViewBox="64">
                <IconStreamsEmptyPlaceholderImage />
              </IconBase>
            </div>
            <p class="mb-2 text-dark-9 text-s-s">No livestreams available.</p>
          </div>
        </div>
        <div v-show="activeSourceTab === 'prerecorded'">
          <div v-if="preRecordedList && preRecordedList.length > 0" class="select-source-list-container">
            <ul class="list-none text-500 select-source-list mt-1">
              <li v-for="(preRecorded, index) in preRecordedList" :key="index">
                <Tooltip :active="preRecorded.destination" :content="preRecorded.destination ? 'Destination is already set for this.' : ''">
                  <FormButton :title="preRecorded.name" :disabled="preRecorded.destination"
                      @click="setSource(preRecorded)"
                    :custom-classes="'w-full flex items-center text-left px-3 py-2 rounded-md text-surface-9 hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'">
                    <span class="truncate">{{ preRecorded.name }}</span>
                    <span v-if="preRecorded.destination" class="ml-auto pl-3 shrink-0">
                      <IconBase fill="none" class="text-surface-7">
                        <IconTV />
                      </IconBase>
                    </span>
                  </FormButton>
                </Tooltip>
              </li>
            </ul>
          </div>
          <div v-else class="text-center">
            <div class="flex justify-center mb-3 mt-3">
              <IconBase fill="none" width="64" height="64" widthViewBox="64" heightViewBox="64">
                <IconStreamsEmptyPlaceholderImage />
              </IconBase>
            </div>
            <p class="mb-2 text-dark-9 text-s-s">No pre-recorded streams available.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from 'lodash';
import { mapGetters } from 'vuex';
import FormButton from '@/components/Atoms/FormButton.vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue';
import IconTV from '@/components/icon/IconTV.vue';
import IconTranscoder from '@/components/icon/IconTranscoder.vue';
import IconStreamsEmptyPlaceholderImage from '@/components/icon/IconStreamsEmptyPlaceholderImage.vue';

export default {
  name: 'SelectLinkedSource',
  components: {
		Tooltip,
    FormButton,
    TabGroup,
    TabItem,
    IconBase,
    IconChevronUpDown,
    IconTV,
    IconTranscoder,
    IconStreamsEmptyPlaceholderImage
  },
  props: {
    stream: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      visible: false,
      source: null,
      activeSourceTab: 'playout',
      sourceTabs: [
        { id: 'playout', label: 'TV Playout' },
        { id: 'transcoder', label: 'Transcoder' },
        { id: 'livestreams', label: 'Livestreams' },
        { id: 'prerecorded', label: 'Pre-recorded' },
      ],
    };
  },

  async mounted() {
    await this.$store.dispatch('Transcoders/getTranscoders');
    document.addEventListener('click', this.handleClickOutside);

    _.forEach(this.playoutList, (item) => {
      if (item.destination === this.stream.name) {
        this.source = item
        this.$emit('init-linked-source', this.source)
      }
    })

    if (!this.source) {
      _.forEach(this.transcoderList, (item) => {
        if (item.destination && item.destination.name === this.stream.name) {
          this.source = item
          this.$emit('init-linked-source', this.source)
        }
      })
    }
    if (!this.source) {
      _.forEach(this.streams, (item) => {
        _.forEach(item.platforms, (platform) => {
          if (platform?.castrName === this.stream.name) {
            this.source = item
            this.$emit('init-linked-source', this.source)
          }
        })
      })
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  computed: {
    ...mapGetters({
      streams: 'Streams/streams',
      transcoderList: 'Transcoders/transcoders'
    }),
    playoutList() {
      return _.filter(this.streams, (item) => item.type === 'tvplayout')
    },
    livestreamList() {
      return _.filter(this.streams, (item) =>
        item.type === 'live' && item._id !== this.stream._id
      )
    },
    preRecordedList() {
      return _.filter(this.streams, (item) =>
        item.type === 'scheduled' && item._id !== this.stream._id
      )
    },
  },

  watch: {
    source() {
      this.$emit('selected-source', this.source)
    },
    stream() {
      if (this.stream.removedSource) this.source = null
    }
  },

  methods: {
    toggleVisible() {
      this.visible = !this.visible
    },
    setSource(source) {
      this.source = source
      this.toggleVisible();
    },
    createNewPlayout() {
      window.location.href = '/app/tv-playout';
    },
    createNewTranscoder() {
      window.location.href = '/app/transcoder';
    },
    handleClickOutside(event) {
      const dropdownContainer = this.$refs.dropdownContainer;
      if (dropdownContainer && !dropdownContainer.contains(event.target)) {
        this.visible = false;
      }
    },
  }
};
</script>

<style scoped>
.select-source-wrapper {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  background-color: var(--surface-2);
  border-radius: .75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.select-source-list-container {
  max-height: 200px;
  overflow-y: auto;
}

:deep(.nav-pills .select-source-tab-link),
:deep(.nav-pills .show>.select-source-tab-link) {
  font-size: var(--font-s-xs);
  font-weight: 500;
  color: var(--c-dark-9);
  padding: .5rem;
  margin-left: 1rem;
  border-radius: .5rem 0 0 .5rem;
}

:deep(.nav-pills .select-source-tab-link.active),
:deep(.nav-pills .show>.select-source-tab-link) {
  color: #fff;
  background-color: var(--c-dark-5);
}

:deep(.rounded-r) {
  border-radius: 0 .5rem .5rem 0;
}

@media (max-width: 767px) {
  .select-source-list {
    max-height: 210px;
    overflow-y: auto;
  }

  .select-source-wrapper {
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 100%;
    min-width: 100%;
  }
}

.list-item {
  display: flex;
  padding: 5px;
}

.list-item:hover {
  color: #fff;
  background-color: var(--c-dark-4);
  border-color: var(--c-dark-4);
}
</style>
